import BreadcrumbWithLink from "components/Common/BreadcrumbWithLink";
import RemoteDataTable from "components/shared-components/RemoteTable/data-table";
import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { MetaTags } from "react-meta-tags";
import { Badge, Card, CardBody } from "reactstrap";
import FilterTable from "./FilterTable";
import CreateMethodModal from "./CreateMethodModal";
import UpdateMethodModal from "./UpdateMethodModal";
import {
  usePaymentMethodList,
  useUpdateStatusPaymentMethod,
} from "hooks/payment-method/usePaymentMethod";

const PaymentMethodPage = () => {
  const { t } = useTranslation(["paymentMethod"]);
  const initParams = () => {
    return {
      sizePerPage: 10,
      page: 1,
      columnFilter: {},
      keywords: "",
      expand: "hub",
      run: false,
      sort: "-maker_date",
    };
  };
  const [isOpenCreateMethodModal, setIsOpenCreateMethodModal] = useState(false);
  const [isOpenUpdateMethodModal, setIsOpenUpdateMethodModal] = useState(false);
  const [paymentMethodChoosenDetail, setPaymentMethodChoosenDetail] =
    useState(undefined);
  const [paymentMethodListParams, setPaymentMethodListParams] =
    useState(initParams);

  const {
    data: paymentMethodList,
    refetch: fetchPaymentMethodList,
    isLoading: isLoadingPaymentMethodList,
  } = usePaymentMethodList({
    params: {
      ...paymentMethodListParams,
      columnFilter: {
        ...paymentMethodListParams.columnFilter,
        hub_id: undefined,
      },
    },
  });

  const updateStatusPaymentMethodMutate = useUpdateStatusPaymentMethod(() => {
    fetchPaymentMethodList();
  });

  const columns = [
    {
      dataField: "id",
      text: t("table.no"),
      formatter: (cell, row, enumObject) => {
        return `${enumObject + 1}`;
      },
    },
    {
      dataField: "hub",
      text: t("table.hub"),
      formatter: (cell, row) => row.hub?.name,
    },
    {
      dataField: "name",
      text: t("table.name"),
    },
    {
      dataField: "code",
      text: t("table.code"),
    },
    {
      dataField: "description",
      text: t("table.note"),
    },
    {
      dataField: "record_status",
      text: t("table.status"),
      formatter: (cell, row) => {
        if (cell === "O") {
          return (
            <Badge color="success" pill>
              {t("actions.active")}
            </Badge>
          );
        } else {
          return (
            <Badge color="danger" pill>
              {t("actions.un_active")}
            </Badge>
          );
        }
      },
    },
  ];

  const rowActions = [
    {
      actionAccess: "",
      icon: <i className="fas fa-lock fa-lg ms-2 text-danger" />,
      text: t("actions.un_active"),
      conditionVisible: {
        record_status: ["O"],
      },
      onClick: row =>
        updateStatusPaymentMethodMutate.mutate({
          id: row.id,
          record_status: "C",
        }),
    },
    {
      actionAccess: "",
      icon: <i className="fas fa-unlock fa-lg ms-2 text-success" />,
      text: t("actions.active"),
      conditionVisible: {
        record_status: ["C"],
      },
      onClick: row =>
        updateStatusPaymentMethodMutate.mutate({
          id: row.id,
          record_status: "O",
        }),
    },
    {
      actionAccess: "",
      icon: <i className="fas fa-pen fa-lg text-primary ms-2" />,
      text: t("actions.edit"),
      onClick: row => onClickUpdateMethod(row),
    },
  ];

  const onClickUpdateMethod = row => {
    setPaymentMethodChoosenDetail(row);
    setIsOpenUpdateMethodModal(true);
  };

  const onTableChange = useCallback((_type, value) => {
    setPaymentMethodListParams(prev => ({
      ...prev,
      page: value.page,
      sizePerPage: value.sizePerPage,
    }));
  }, []);

  const onSearchAllHandle = useCallback(value => {
    setPaymentMethodListParams(prev => ({
      ...prev,
      keywords: value,
    }));
  }, []);

  useEffect(() => {
    if (paymentMethodListParams.run) {
      fetchPaymentMethodList();
    }
    return () => {};
  }, [paymentMethodListParams]);

  return (
    <div className="page-content">
      <MetaTags>
        <title>{t("title.manager")}</title>
      </MetaTags>
      <div className="container-fluid">
        <BreadcrumbWithLink
          title={t("title.list", { target: t("paymentMethod") })}
          breadcrumbItems={[
            { name: t("title.manager") },
            { name: t("title.list", { target: t("paymentMethod") }) },
          ]}
        />
        <Card>
          <CardBody>
            <RemoteDataTable
              keyField="id"
              data={paymentMethodList?.items || []}
              columns={columns}
              listRowAction={rowActions}
              isShowRowIndex={false}
              onTableChange={onTableChange}
              loading={isLoadingPaymentMethodList}
              onSearchAllHandle={onSearchAllHandle}
              page={paymentMethodList?.meta?.currentPage}
              totalSize={paymentMethodList?.meta?.totalCount}
              searchSticky
              sizePerPage={paymentMethodList?.meta?.perPage}
            >
              <FilterTable
                paymentMethodListParams={paymentMethodListParams}
                setPaymentMethodListParams={setPaymentMethodListParams}
                openCreateMethodModal={() => setIsOpenCreateMethodModal(true)}
              />
            </RemoteDataTable>
          </CardBody>
        </Card>
        <CreateMethodModal
          isOpenModal={isOpenCreateMethodModal}
          onCloseModal={() => setIsOpenCreateMethodModal(false)}
          fetchPaymentMethodList={fetchPaymentMethodList}
        />
        <UpdateMethodModal
          isOpenModal={isOpenUpdateMethodModal}
          onCloseModal={() => {
            setIsOpenUpdateMethodModal(false);
            setPaymentMethodChoosenDetail(undefined);
          }}
          detail={paymentMethodChoosenDetail}
          fetchPaymentMethodList={fetchPaymentMethodList}
        />
      </div>
    </div>
  );
};

export default PaymentMethodPage;
